/* Left sidebar: brand, navigation links with counts, sources, settings, and user profile. */

import { useState } from "react";
import { NavLink } from "react-router-dom";
import { UserButton } from "@clerk/clerk-react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Rss, ListVideo, Archive, PlusCircle, TrendingUp, Settings, X, Sparkles, MessageSquare, HelpCircle, ExternalLink, Sunrise } from "lucide-react";
import { FeedbackDialog } from "@/components/feedback/FeedbackDialog";
import { KiwiIcon } from "@/components/brand/KiwiIcon";
import { KiwiSkinRenderer } from "@/components/brand/KiwiSkinRenderer";
import { useActiveKiwiSkin } from "@/hooks/useActiveKiwiSkin";
import { useFeatureGate } from "@/hooks/useFeatureGate";
import { TIER_DISPLAY_NAMES } from "@/lib/featureGates";
import { GoalsBar } from "@/components/goals/GoalsBar";
import { GoalsInfo } from "@/components/feature-info";
import { DailyStreakBadge } from "@/components/goals/DailyStreakBadge";
import { SidebarTodayStatsCard } from "@/components/layout/SidebarTodayStatsCard";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { getLocalMidnight } from "@/lib/getLocalMidnight";
import { RoutePath, ExternalUrl } from "@/routePaths";

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  count?: number | string;
}

function NavItem({ to, icon, label, count }: NavItemProps) {
  return (
    <NavLink to={to} className="block">
      {({ isActive }) => (
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-start gap-2 px-3",
            isActive
              ? "bg-sidebar-accent text-sidebar-accent-foreground hover:!bg-sidebar-accent"
              : "hover:!bg-sidebar-accent/35",
          )}
          asChild
        >
          <span>
            {icon}
            <span className="flex-1 text-left">{label}</span>
            {count !== undefined && (
              <Badge
                variant="secondary"
                className={cn(
                  isActive
                    ? "bg-sidebar-primary text-sidebar-primary-foreground"
                    : "bg-transparent text-sidebar-foreground",
                )}
              >
                {count}
              </Badge>
            )}
          </span>
        </Button>
      )}
    </NavLink>
  );
}

function UpgradeButton() {
  const { tier, isProAi, isLoading } = useFeatureGate();

  if (isLoading || isProAi) return null;

  const label = tier === "free" ? "Upgrade to Pro" : "Upgrade to Pro AI";

  return (
    <NavLink to={RoutePath.Pricing} className="block">
      <Button variant="ghost" className="w-full justify-start gap-2 px-3">
        <Sparkles className="size-4 text-primary" />
        <span className="flex-1 text-left text-primary">{label}</span>
      </Button>
    </NavLink>
  );
}

function FeedbackButton() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button variant="ghost" className="w-full justify-start gap-2 px-3" onClick={() => setOpen(true)}>
        <MessageSquare className="size-4" />
        <span className="flex-1 text-left">Send Feedback</span>
      </Button>
      <FeedbackDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

function HelpDocsButton() {
  return (
    <a href={ExternalUrl.Docs} target="_blank" rel="noopener noreferrer" className="block">
      <Button variant="ghost" className="w-full justify-start gap-2 px-3">
        <HelpCircle className="size-4" />
        <span className="flex-1 text-left">Help & Docs</span>
        <ExternalLink className="size-3 text-muted-foreground" />
      </Button>
    </a>
  );
}

function AccountRow() {
  const { tier, isFree, isLoading } = useFeatureGate();

  return (
    <div className="flex items-center gap-2 px-3 py-2">
      <UserButton appearance={{ elements: { avatarBox: "h-8 w-8" } }} />
      <span className="flex-1 text-xs text-muted-foreground">Account</span>
      {!isLoading && (
        isFree ? (
          <NavLink to={RoutePath.Pricing}>
            <Badge variant="secondary" className="text-[10px] h-4 px-1.5 bg-muted text-muted-foreground hover:bg-muted/80 cursor-pointer">
              Free
            </Badge>
          </NavLink>
        ) : (
          <Badge variant="secondary" className="text-[10px] h-4 px-1.5 bg-muted text-muted-foreground">
            {TIER_DISPLAY_NAMES[tier]}
          </Badge>
        )
      )}
    </div>
  );
}

interface AppSidebarProps {
  onClose?: () => void;
  className?: string;
}

export function AppSidebar({ onClose, className }: AppSidebarProps = {}) {
  const [goalsInfoOpen, setGoalsInfoOpen] = useState(false);
  const allCounts = useQuery(api.feedItems.getAllCounts);
  const { activeSkin, isLoading: isSkinLoading } = useActiveKiwiSkin();
  const goalsData = useQuery(api.tagGoals.getAllWithStreaks, { todayTimestamp: getLocalMidnight() });
  const overallStreak = goalsData?.overallStreak ?? 0;
  const todayGoalsComplete = goalsData?.todayHasCompletion ?? false;
  return (
    <aside
      className={cn(
        "flex flex-col w-full bg-background text-sidebar-foreground h-full",
        className,
      )}
    >
      <div className="px-4 py-4">
        <div className="flex items-center gap-2">
          {isSkinLoading ? (
            <Skeleton className="size-6 rounded-full" />
          ) : activeSkin ? (
            <KiwiSkinRenderer config={activeSkin} className="size-6" />
          ) : (
            <KiwiIcon className="size-6 text-primary" />
          )}
          <span className="flex-1 text-lg font-bold">FeedKiwi</span>
          <DailyStreakBadge variant="flame" streak={overallStreak} active={todayGoalsComplete} />
          {onClose && (
            <Button variant="ghost" size="icon-sm" onClick={onClose}>
              <X className="size-4" />
            </Button>
          )}
        </div>
      </div>

      <nav className="flex-1 px-2 space-y-0.5">
        <NavItem
          to={RoutePath.DailyOverview}
          icon={<Sunrise className="size-4" />}
          label="Daily Overview"
        />
        <div data-guide="nav-feed">
          <NavItem
            to={RoutePath.Feed}
            icon={<Rss className="size-4" />}
            label="Feed"
            count={allCounts?.feed.sidebarCount ?? undefined}
          />
        </div>
        <NavItem
          to={RoutePath.Queue}
          icon={<ListVideo className="size-4" />}
          label="Queue"
          count={allCounts?.queue.all ?? undefined}
        />
        <NavItem
          to={RoutePath.Backlog}
          icon={<Archive className="size-4" />}
          label="Backlog"
          count={allCounts?.backlog.total ?? undefined}
        />

        <Separator className="my-2" />

        <div data-guide="nav-stats">
          <NavItem
            to={RoutePath.Stats}
            icon={<Sparkles className="size-4" />}
            label="Insights"
          />
        </div>
        <div data-guide="nav-sources">
          <NavItem
            to={RoutePath.Sources}
            icon={<PlusCircle className="size-4" />}
            label="Sources"
          />
        </div>
        <NavItem
          to={RoutePath.Charts}
          icon={<TrendingUp className="size-4" />}
          label="Charts"
        />

        <div className="px-1 mt-6" data-guide="goals-section">
          <div className="rounded-lg border border-sidebar-border/60 bg-sidebar-accent/5 px-2 py-2">
            <div className="flex items-center justify-between px-2 mb-2">
              <p className="text-xs font-medium text-muted-foreground">Goals</p>
              <button
                onClick={() => setGoalsInfoOpen(true)}
                className="text-muted-foreground/40 hover:text-muted-foreground transition-colors"
                aria-label="How Goals work"
              >
                <HelpCircle className="size-3.5" />
              </button>
            </div>
            <GoalsBar />
            <div className="mt-3 pt-3 border-t border-sidebar-border/50">
              <SidebarTodayStatsCard />
            </div>
          </div>
        </div>
      </nav>

      <div className="px-2 pb-4 space-y-1">
        <UpgradeButton />
        <FeedbackButton />
        <HelpDocsButton />
        <div data-guide="nav-settings">
          <NavItem
            to={RoutePath.Settings}
            icon={<Settings className="size-4" />}
            label="Settings"
          />
        </div>

        <AccountRow />
      </div>
      <GoalsInfo open={goalsInfoOpen} onOpenChange={setGoalsInfoOpen} />
    </aside>
  );
}
